import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import CircularProgress from '@mui/material/CircularProgress';

import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconsSend from '@mui/icons-material/Send';

import FormControlLabel from '@mui/material/FormControlLabel';

import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import Checkbox from '@mui/material/Checkbox';
import { useMergeCompanyKeywordClassificationMutation } from '../../../../../graphql/mutation/companyKeywordClassification/mergeCompanyKeywordClassificationMutation';
import { useCompanyKeywordClassificationQuery } from '../../../../../graphql/query/companyKeywordClassification/useCompanyKeywordClassificationQuery';
import useNotification from '../../../../components/_hook/useNotification';
import DialogConfirm from '../../../../components/_dialog/DialogConfirm';
import ButtonSubmit from '../../../../components/_button/ButtonSubmit';
import useForm from '../../../../components/_hook/useForm';
import NotFound from '../../../../components/_error/NotFound';

interface IProps {
  companyId: string;
  companyClassificationIds: string[];
  onSubmit?: () => void;
}

export default function FormCompanyKeywordMergeClassification(props: IProps) {
  const { t } = useTranslation();
  const { companyClassificationIds, onSubmit, companyId } = props;
  const { notification } = useNotification();
  const [isOpenConfirmMergeDialog, setIsOpenConfirmMergeDialog] = useState(
    false
  );
  const { data, loading } = useCompanyKeywordClassificationQuery(
    `
			_id
			id
        	company
        	status
			classificationData {
			  _id
			  name
              slug
              la
              status
			}
		`,
    { variables: { filter: { _ids: companyClassificationIds } } }
  );
  const [
    mergeCompanyKeywordClassificationMutation,
  ] = useMergeCompanyKeywordClassificationMutation('isSuccess', {
    onCompleted: () => {
      notification.success(t('done'));
      if (onSubmit) {
        onSubmit();
      }
    },
    onError: (e) => {
      notification.error(e.message);
    },
  });
  const { values, setValue, handleSubmit, errors, isSubmitting } = useForm<{
    mergeIntoCompanyKeywordClassificationId: string;
    mergingCompanyKeywordClassificationIds: string[];
  }>({
    defaultValues: {
      mergeIntoCompanyKeywordClassificationId: '',
      mergingCompanyKeywordClassificationIds: [],
    },
    onSubmit: async (tempValues) => {
      setIsOpenConfirmMergeDialog(false);
      await mergeCompanyKeywordClassificationMutation({
        variables: {
          companyId,
          mergeIntoCompanyKeywordClassificationId:
            tempValues.mergeIntoCompanyKeywordClassificationId,
          mergingCompanyKeywordClassificationIds:
            tempValues.mergingCompanyKeywordClassificationIds,
        },
      });
    },
    onValidate: (tempValues) => {
      const newErrors: Record<string, string> = {};

      if (!tempValues.mergeIntoCompanyKeywordClassificationId) {
        newErrors.mergeIntoCompanyKeywordClassificationId = t(
          'please_select_the_final__type__',
          {
            name: t('classification'),
          }
        );
      }

      if (!tempValues.mergingCompanyKeywordClassificationIds.length) {
        newErrors.mergingCompanyKeywordClassificationIds = t(
          'please_select_at_least_one__name__',
          {
            name: `${t('classification').toLowerCase()}`,
          }
        );
      }

      return newErrors;
    },
  });

  if (loading) {
    return (
      <Grid container justifyContent="center">
        <CircularProgress size={30} color="inherit" />
      </Grid>
    );
  }

  if (!data || data.companyKeywordClassifications.items.length === 0) {
    return <NotFound />;
  }

  const classifications = data.companyKeywordClassifications.items;

  const possibleMergingClassifications = classifications.filter(
    (classification) =>
      classification._id !== values.mergeIntoCompanyKeywordClassificationId
  );

  return (
    <>
      <DialogConfirm
        title={t(
          'are_you_sure_you_want_to__action__once_confirmed_this_cannot_be_undone',
          { action: t('merge') }
        )}
        open={isOpenConfirmMergeDialog}
        onClose={() => setIsOpenConfirmMergeDialog(false)}
        onConfirm={handleSubmit}
      />
      <DialogTitle>
        {t('merge')} {t('classification')}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl>
              <FormLabel>
                {t('please_select_the_final__type__', {
                  type: t('classification'),
                })}
              </FormLabel>
              <RadioGroup
                aria-label="category"
                name="category"
                value={values.mergeIntoCompanyKeywordClassificationId}
                onChange={(e) => {
                  setValue(
                    'mergeIntoCompanyKeywordClassificationId',
                    e.target.value
                  );
                  setValue('mergingCompanyKeywordClassificationIds', []);
                }}
              >
                {classifications.map((classification) => (
                  <FormControlLabel
                    key={classification._id}
                    value={classification._id}
                    control={<Radio color="primary" />}
                    label={
                      classification.classificationData
                        ? classification.classificationData.name
                        : '-'
                    }
                  />
                ))}
              </RadioGroup>
              <FormHelperText error>
                {errors.mergeIntoCompanyKeywordClassificationId}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl>
              <FormLabel>
                {t('add_below__type__for_auto_merging', {
                  type: t('classification'),
                })}
              </FormLabel>
              <FormGroup>
                {possibleMergingClassifications.map((classification) => (
                  <FormControlLabel
                    key={classification._id}
                    control={
                      <Checkbox
                        checked={values.mergingCompanyKeywordClassificationIds.includes(
                          classification._id
                        )}
                        color="primary"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setValue('mergingCompanyKeywordClassificationIds', [
                              ...values.mergingCompanyKeywordClassificationIds,
                              ...[classification._id],
                            ]);
                          } else {
                            setValue(
                              'mergingCompanyKeywordClassificationIds',
                              values.mergingCompanyKeywordClassificationIds.filter(
                                (mergingId) => mergingId !== classification._id
                              )
                            );
                          }
                        }}
                        value={classification._id}
                      />
                    }
                    label={
                      classification.classificationData
                        ? classification.classificationData.name
                        : '-'
                    }
                  />
                ))}
              </FormGroup>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <ButtonSubmit
          fullWidth
          variant="contained"
          color="primary"
          onClick={() => setIsOpenConfirmMergeDialog(true)}
          isSubmitting={isSubmitting}
          label={t('submit')}
          icon={<IconsSend />}
        />
      </DialogActions>
    </>
  );
}
